import { useState, useEffect, ChangeEvent, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Blog,
  BlogPayload,
  BlogStatus,
  createBlog,
  fetchBlogById,
  updateBlog,
  uploadBlogCover
} from "../../services/blogService";
import RichTextEditor from "../../components/ui/RichTextEditor";
import { Image as ImageIcon, Sparkles, Upload, Trash2, RotateCcw } from "lucide-react";

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--text3)" }}>{hint}</span>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
  color: "var(--text)",
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  width: "100%",
  boxSizing: "border-box",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "none",
  background: "var(--card-solid)",
  cursor: "pointer",
};

const taStyle: React.CSSProperties = { ...inputStyle, resize: "vertical" };

function getHtmlFromBody(bodyVal: any): string {
  if (typeof bodyVal === "string") {
    return bodyVal;
  }
  if (Array.isArray(bodyVal)) {
    return bodyVal
      .map((s: any) => {
        if (s.type === "heading") return `<h2>${s.text}</h2>`;
        if (s.type === "paragraph") return `<p>${s.text}</p>`;
        if (s.type === "callout") return `<blockquote><p>${s.text}</p></blockquote>`;
        if (s.type === "divider") return `<hr />`;
        if (s.type === "list") {
          return `<ul>${s.items.map((i: string) => `<li>${i}</li>`).join("")}</ul>`;
        }
        return "";
      })
      .join("");
  }
  return "<p><br></p>";
}

export default function ManageBlog() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");
  const isEdit = !!editId;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState<{ visible: boolean; message: string; type: "success" | "error" }>({
    visible: false,
    message: "",
    type: "success"
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const [subtitle, setSubtitle]               = useState("");
  const [bodyHtml, setBodyHtml]               = useState("<p><br></p>");
  const [status, setStatus]                   = useState<BlogStatus>("draft");

  const [seoTitle, setSeoTitle]               = useState("");
  const [seoDescription, setSeoDescription]   = useState("");
  const [slug, setSlug]                       = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [coverImageUrl, setCoverImageUrl]     = useState("");
  const [coverGradient, setCoverGradient]     = useState("");
  const [uploadingImage, setUploadingImage]   = useState(false);

  const [showImageTab, setShowImageTab]       = useState(true);
  const [showGradientTab, setShowGradientTab] = useState(false);

  // Auto-generate slug from SEO Title only if user hasn't manually edited it
  useEffect(() => {
    if (!isEdit && !slugManuallyEdited) {
      setSlug(
        seoTitle
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
      );
    }
  }, [seoTitle, isEdit, slugManuallyEdited]);

  // Load blog if editing
  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    fetchBlogById(editId)
      .then((b: Blog) => {
        setSubtitle(b.subtitle || "");
        setBodyHtml(getHtmlFromBody(b.body));
        setStatus(b.status || "draft");
        setSeoTitle(b.seoTitle || "");
        setSeoDescription(b.seoDescription || "");
        setSlug(b.slug || "");
        setSlugManuallyEdited(true);

        setCoverImageUrl(b.coverImageUrl || "");
        setCoverGradient(b.coverGradient || "");

        if (b.coverImageUrl) setShowImageTab(true);
        else setShowImageTab(false);

        if (b.coverGradient) setShowGradientTab(true);
        else setShowGradientTab(false);
      })
      .catch(() => showToast("Failed to load blog post.", "error"))
      .finally(() => setLoading(false));
  }, [editId]);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const res = await uploadBlogCover(file);
      if (res && res.url) {
        setCoverImageUrl(res.url);
        showToast("Image uploaded successfully.");
      } else {
        showToast("Upload failed.", "error");
      }
    } catch (err: any) {
      showToast(err?.message || "Failed to upload image.", "error");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!seoTitle.trim()) { showToast("SEO Title is required.", "error"); return; }
    if (!bodyHtml.trim() || bodyHtml === "<p><br></p>") { showToast("Content is required.", "error"); return; }
    if (!slug.trim()) { showToast("Slug is required.", "error"); return; }

    setSaving(true);
    setSaveError(null);

    const payload: BlogPayload = {
      slug: slug.trim(),
      subtitle: subtitle.trim() || undefined,
      body: bodyHtml,
      status,
      seoTitle: seoTitle.trim(),
      seoDescription: seoDescription.trim() || subtitle.trim(),
      coverImageUrl: showImageTab ? coverImageUrl.trim() : "",
      coverGradient: showGradientTab ? coverGradient.trim() : "",
    };

    try {
      if (isEdit) {
        await updateBlog(editId!, payload);
        showToast("Blog post updated successfully.");
        setTimeout(() => navigate("/content/blogs"), 1200);
      } else {
        await createBlog(payload);
        showToast("Blog post created successfully.");
        setTimeout(() => navigate("/content/blogs"), 1200);
      }
    } catch (err: any) {
      setSaveError(err?.message || "Save failed. Please try again.");
      showToast("Save failed. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "var(--text3)", fontSize: 14 }}>Loading…</div>
  );

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        input::placeholder, textarea::placeholder { color: var(--text3); }
        input:focus, textarea:focus, select:focus { border-color: rgba(191,61,12,0.5) !important; box-shadow: 0 0 0 2px rgba(191,61,12,0.08); }
        select option { background: var(--card-solid) !important; color: var(--text) !important; }
      `}</style>

      {toast.visible && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 600, background: toast.type === "success" ? "#1a2e1a" : "#2e1a1a", border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : "rgba(224,82,82,0.25)"}`, borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : "#e08b8b" }}>{toast.type === "success" ? "✓" : "✕"} &nbsp;{toast.message}</span>
        </div>
      )}

      <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
            <button onClick={() => navigate("/content/blogs")} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 12px", color: "var(--text3)", fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
              ← Back
            </button>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 2 }}>Blogs</div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.3px" }}>{isEdit ? "Edit Blog Post" : "Add New Blog Post"}</h1>
            </div>
          </div>

          {saveError && (
            <div style={{ background: "rgba(224,82,82,0.08)", border: "1px solid rgba(224,82,82,0.25)", color: "#e08b8b", padding: "12px 16px", borderRadius: 10, marginBottom: 20, fontSize: 13 }}>
              ✕ {saveError}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24, alignItems: "start" }}>

            {/* Left Main Column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {/* SEO Card */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>SEO Information</div>
                <Field label="SEO Title *">
                  <input value={seoTitle} onChange={(e: ChangeEvent<HTMLInputElement>) => setSeoTitle(e.target.value)} placeholder="SEO Meta Title (recommended 50-60 chars)" style={inputStyle} />
                </Field>
                  <Field label="Blog title">
                  <input value={subtitle} onChange={(e: ChangeEvent<HTMLInputElement>) => setSubtitle(e.target.value)} placeholder="A Blog Title" style={inputStyle} />
                </Field>
                <Field label="SEO Description" hint="Defaults to the subtitle if empty">
                  <textarea value={seoDescription} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSeoDescription(e.target.value)} placeholder="SEO Meta Description (recommended 120-160 chars)" style={{ ...taStyle, minHeight: 70 }} />
                </Field>
                <Field label="Slug *">
                  <div style={{ position: "relative" }}>
                    <input
                      value={slug}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setSlug(e.target.value);
                        setSlugManuallyEdited(true);
                      }}
                      placeholder="how-enzymes-improve-bioethanol-yield"
                      style={inputStyle}
                    />
                    {slugManuallyEdited && !isEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          setSlugManuallyEdited(false);
                          setSlug(seoTitle.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
                        }}
                        title="Reset to auto-generate from SEO Title"
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 11, color: "var(--or)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", padding: "2px 4px", lineHeight: 1 }}
                      >
                        <RotateCcw size={11} style={{ marginRight: 2, display: "inline-block", verticalAlign: "middle" }} /> Reset
                      </button>
                    )}
                  </div>
                </Field>
              </div>

                       {/* Rich Text Editor */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Body Content</div>
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write the main blog content here..." />
              </div>

            </div>

            {/* Right Sidebar Column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20, position: "sticky", top: 24 }}>

              {/* Publish Action Card */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Publish</div>

                <Field label="Status">
                  <select value={status} onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value as BlogStatus)} style={selectStyle}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </Field>

                <button onClick={handleSubmit} disabled={saving} style={{ padding: "11px 18px", borderRadius: 9, border: "1px solid rgba(191,61,12,0.35)", background: saving ? "rgba(191,61,12,0.3)" : "linear-gradient(135deg, #BF3D0C, #E8593C)", color: "#fff", fontSize: 14, fontWeight: 500, cursor: saving ? "not-allowed" : "pointer", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 2px 12px rgba(191,61,12,0.25)" }}>
                  {saving ? "Saving…" : isEdit ? "Update Post" : "Create Post"}
                </button>
                <button onClick={() => navigate("/content/blogs")} style={{ padding: "9px 18px", borderRadius: 9, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Cancel</button>
              </div>

              {/* Cover & Appearance option panel */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Cover & Appearance</div>
                
                {/* Checkboxes for Options */}
                <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImageTab(true);
                      setShowGradientTab(false);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 4px",
                      borderRadius: 8,
                      border: `1px solid ${showImageTab ? "var(--or)" : "var(--border)"}`,
                      background: showImageTab ? "rgba(191, 61, 12, 0.08)" : "transparent",
                      color: showImageTab ? "var(--or)" : "var(--text3)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    <ImageIcon size={12} /> Image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowGradientTab(true);
                      setShowImageTab(false);
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 4px",
                      borderRadius: 8,
                      border: `1px solid ${showGradientTab ? "var(--or)" : "var(--border)"}`,
                      background: showGradientTab ? "rgba(191, 61, 12, 0.08)" : "transparent",
                      color: showGradientTab ? "var(--or)" : "var(--text3)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    <Sparkles size={12} /> Grad
                  </button>
                </div>

                {/* Option 1: Cover Image Upload */}
                {showImageTab && (
                  <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "rgba(255, 255, 255, 0.01)" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 8 }}>Cover Image</div>
                    
                    {coverImageUrl ? (
                      <div>
                        <div style={{ position: "relative", borderRadius: 6, overflow: "hidden", height: 80, border: "1px solid var(--border)", marginBottom: 8 }}>
                          <img src={coverImageUrl} alt="Cover Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button
                            type="button"
                            onClick={() => setCoverImageUrl("")}
                            style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", padding: 4, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}
                            title="Remove image"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ padding: "4px 8px", fontSize: 11, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", borderRadius: 4, cursor: "pointer", width: "100%" }}
                        >
                          Replace Image
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{ border: "1px dashed var(--border)", borderRadius: 6, height: 80, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 6, color: "var(--text3)" }}
                      >
                        <Upload size={14} />
                        <span style={{ fontSize: 11 }}>{uploadingImage ? "Uploading..." : "Upload Cover Image"}</span>
                      </div>
                    )}
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" style={{ display: "none" }} />
                  </div>
                )}

                {/* Option 2: Gradient Background */}
                {showGradientTab && (
                  <Field label="Cover Gradient" hint="E.g. linear-gradient(135deg, #1a1208 0%, #3d2a0a 100%)">
                    <input value={coverGradient} onChange={(e: ChangeEvent<HTMLInputElement>) => setCoverGradient(e.target.value)} placeholder="linear-gradient(...)" style={inputStyle} />
                  </Field>
                )}
              </div>

              {/* Cover Live Preview Card */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
                <div style={{
                  height: 100,
                  backgroundImage: showImageTab && coverImageUrl ? `url(${coverImageUrl})` : (showGradientTab && coverGradient ? coverGradient : "none"),
                  backgroundColor: !coverImageUrl ? "#1a1917" : "transparent",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "flex-end",
                  padding: "12px 16px"
                }}></div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)", marginBottom: 4, lineHeight: 1.4 }}>{seoTitle || "Blog title preview"}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>{subtitle || "Subtitle preview text..."}</div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>
    </>
  );
}